import {
  AfterViewChecked,
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { ClientListService } from '../../../../services/client-list/client-list.service';
import { DisplayService } from '../../../../services/reports/display.service';
import { ReportsService } from '../../../../services/reports/reports.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { ValidatorsEx } from '../../../../validators/validators-ex';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { InputFieldComponent } from '../../../core/fields/input-field.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { ReportFieldsComponent } from '../report-fields.component';
import { SelectionCriteriaComponent } from '../selection-criteria.component';
import { SortOptionsComponent } from '../sort-options.component';

@Component({
  selector: 'app-report-detail-step',
  imports: [
    PageHeaderComponent,
    ActionRowComponent,
    ButtonComponent,
    InputFieldComponent,
    SelectFieldComponent,
    SelectionCriteriaComponent,
    ReportFieldsComponent,
    SortOptionsComponent,
    LoadingPanelComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <div class="tw-py-8">
      <div class="tw-pb-4">
        <app-page-header
          [label]="reports.currentReport()?.reportTemplate?.title"
          labelCase="none"
          headerSize="h2"
          [helpText]="reports.currentReport()?.reportTemplate?.description"
        >
          <ng-template #right>
            <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
          </ng-template>
        </app-page-header>

        <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
          <app-input-field
            [control]="fg.controls.requestName"
            label="Name"
            labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
            valueClasses="tw-basis-full sm:tw-basis-[70%]"
          />
          @if (showClient()) {
            <app-select-field
              class="tw-block tw-min-w-0 tw-max-w-full"
              label="Client"
              [options]="clientList"
              [control]="fg.controls.client"
              [disabled]="isEditMode()"
              valueClasses="sm:tw-max-w-[500px]"
            />
          }
        </div>
      </div>

      <app-loading-panel
        [state]="loadingState()"
        loadingStyle="Box"
        [loadingHeight]="200"
      >
        <ng-template #loaded>
          @if (display.showSelectionCriteria()) {
            <div class="tw-pb-4">
              <app-selection-criteria #selectionCriteria />
            </div>
          }
          @if (display.showReportFields()) {
            <div class="tw-py-8 tw-overflow-x-auto">
              <app-page-header label="Report fields" headerSize="h3" />
              <div>
                <app-report-fields #reportFields />
              </div>
            </div>
          }
          @if (display.showSortOptions()) {
            <div class="tw-py-8 tw-overflow-x-auto">
              <app-sort-options #sortOptions />
            </div>
          }
        </ng-template>
      </app-loading-panel>
    </div>
    <app-action-row>
      <app-button right1 emphasis="Low" label="Previous" (click)="previous()" />
      <app-button
        right2
        label="Next"
        [disabled]="disableNext()"
        (click)="next()"
      />
    </app-action-row>
  `,
  styles: `
    :host ::ng-deep app-select-field {
      display: block;
      min-width: 0;
    }
    :host ::ng-deep .p-select-label > .option-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class ReportDetailStepComponent
  extends BaseComponent
  implements OnInit, AfterViewChecked
{
  //child components
  @ViewChild('selectionCriteria')
  selectionCriteria: SelectionCriteriaComponent;

  @ViewChild('reportFields')
  reportFields: ReportFieldsComponent;

  @ViewChild('sortOptions')
  sortOptions: SortOptionsComponent;

  @Input()
  stepper: StepperComponent;

  clientList: ListItem[];
  private lastClient = '';
  private singleClient = false;

  loadingState = signal<LoadingState>('Loaded');

  showClient = computed(() => this.display.showClientDropdown());

  isEditMode = computed(() => this.reports.isEditPersonalizedMode());

  fg = new FormGroup({
    requestName: new FormControl<string>(null, [
      Validators.required,
      ValidatorsEx.alphaNumericWith(
        '/-_ ',
        'Slashes (/), dashes (-), and underscores (_) are the only special characters allowed in report name.',
      ),
    ]),
    client: new FormControl<string>(null, [Validators.required]),
  });

  disableNext = signal(true);

  ngAfterViewChecked() {
    const result =
      this.fg?.invalid ||
      this.selectionCriteria?.invalid ||
      this.reportFields?.invalid ||
      this.sortOptions?.invalid;
    this.disableNext.set(!!result);
  }

  reports = inject(ReportsService);
  display = inject(DisplayService);
  private clientListService = inject(ClientListService);
  private snackbar = inject(Snackbar);

  async ngOnInit() {
    await this.initializeReportData();
    this.initializeRequestName();
    await this.initializeClientDropdown();
  }

  private async initializeReportData(): Promise<void> {
    const report = this.reports.currentReport();

    if (report && !report.reportRequest?.controlsMap) {
      const template = report.reportTemplate;

      if (this.reports.isEditScheduledDeliveryMode()) {
        this.reports.initializeReport(report);
      } else if (this.reports.isEditPersonalizedMode()) {
        this.reports.initializeReport(report);
      } else if (template && !template.requiresClient) {
        const initializedReport = await this.getReport(
          template.id,
          template.model,
          undefined,
        );
        this.reports.initializeReport(initializedReport);
      }
    }
  }

  private initializeRequestName(): void {
    this.fg.controls.requestName.setValue('');
    if (this.reports.selectedClient() !== '') {
      this.fg.controls.requestName.setValue(
        this.reports.currentReport()?.reportInfo?.requestName || '',
      );
    }
  }

  private async initializeClientDropdown(): Promise<void> {
    if (!this.showClient()) {
      this.clearClientValidation();
      return;
    }

    if (this.fg.controls.requestName.invalid || this.isEditMode()) {
      this.fg.controls.client.disable();
    } else {
      this.fg.controls.client.enable();
    }

    this.fg.controls.client.addValidators(Validators.required);
    this.loadingState.set('Loading');

    this.clientList = await this.clientListService.load();

    if (this.clientListService.isSingleClient() && !this.isEditMode()) {
      this.singleClient = true;
      const value = this.clientList[0].value;
      this.fg.controls.client.setValue(value, { emitEvent: false });
      this.fg.controls.client.disable();
      this.reports.selectedClient.set(value);
      this.lastClient = value;
    }

    this.setupFormListeners();

    const clientNumber = this.reports.selectedClient();
    if (clientNumber) {
      this.fg.controls.client.setValue(clientNumber, { emitEvent: false });
      this.lastClient = clientNumber;
    }

    this.loadingState.set('Loaded');
  }

  private clearClientValidation(): void {
    this.fg.controls.client.clearValidators();
    this.fg.controls.client.updateValueAndValidity();
  }

  private setupFormListeners(): void {
    // Don't reload report UI when editing - they already have all their data
    if (this.reports.isEditScheduledDeliveryMode() || this.isEditMode()) {
      return;
    }

    this.dispose.subs = this.fg.controls.requestName.valueChanges.subscribe(
      async () => {
        if (this.singleClient) {
          if (this.fg.controls.requestName.valid) {
            await this.reloadReportForClient(this.lastClient);
          }
          return;
        }
        if (this.fg.controls.requestName.valid) {
          this.fg.controls.client.enable({ emitEvent: false });
        } else {
          this.fg.controls.client.disable({ emitEvent: false });
        }
      },
    );

    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      async (clientValue) => {
        if (clientValue) {
          await this.reloadReportForClient(clientValue);
        } else {
          await this.reloadReportForClient('');
        }
      },
    );
  }

  private async getReport(
    reportId: string,
    model: string,
    contractNumber?: string,
  ) {
    return this.reports.getReportUI(reportId, model, contractNumber);
  }

  private async reloadReportForClient(contractNumber: string) {
    if (
      this.reports.selectedClient() === '' ||
      contractNumber === this.reports.selectedClient()
    ) {
      this.loadingState.set('Loading');
      const contractStr =
        contractNumber !== '' ? contractNumber.toString() : '';

      if (contractStr) {
        const reportId = this.reports.currentReport()?.reportTemplate?.id;
        if (reportId) {
          const accessResult = await this.api.reports.checkReportAccess(
            reportId,
            contractStr,
          );
          if (!accessResult.hasAccess) {
            this.snackbar.show(
              accessResult.reason ||
                'You do not have access to this report for the selected client.',
              'Error',
              'fa-light fa-exclamation-triangle',
              5000,
            );
            this.fg.controls.client.setValue(this.lastClient, {
              emitEvent: false,
            });
            this.loadingState.set('Loaded');
            return;
          }
        }
      }
      this.reports.selectedClient.set(contractStr);
      this.lastClient = contractStr;
      const currentReport = this.reports.currentReport();

      const reportUIResponse = await this.reports.getReportUI(
        currentReport.reportTemplate.id,
        currentReport.reportTemplate.model,
        contractStr,
      );

      this.reports.initializeReport(reportUIResponse);

      const selectedClient = this.clientList?.find(
        (c) => c.value === contractStr,
      );
      const clientName = selectedClient?.description || contractStr;

      this.reports.currentReport.update((report) => {
        return {
          ...report,
          reportInfo: {
            ...report.reportInfo!,
            contractNumber: contractStr,
            clientName: clientName,
          },
        };
      });

      if (this.selectionCriteria) {
        this.selectionCriteria.loadAllConfigurations();
        this.selectionCriteria.reloadChildComponents();
      }
      if (this.sortOptions) {
        this.sortOptions.reload();
      }

      this.loadingState.set('Loaded');
    } else {
      // Defer the revert to next tick: PrimeNG's Select.updateModel() sets its
      // internal modelValue signal AFTER calling onModelChange (which triggers
      // this valueChanges subscription), so a synchronous setValue gets overwritten.
      setTimeout(() => {
        this.fg.controls.client.setValue(this.lastClient, {
          emitEvent: false,
        });
      });

      this.confirm.show({
        label: 'Change client',
        message: `Are you sure you want to change the selected client?  All report configurations will be discarded.`,
        width: '35rem',
        confirm: async () => {
          this.fg.controls.client.setValue(contractNumber, {
            emitEvent: false,
          });
          this.lastClient = contractNumber;
          let report = this.reports.currentReport();
          const template = report.reportTemplate;
          this.reports.clearReport();
          this.reports.currentReport.set(report);
          this.reports.selectedClient.set(contractNumber);
          report = this.reports.createNewReport(template);
          this.reports.currentReport.set(report);
          this.stepper.resetStepsAfter(1);
          await this.reloadReportForClient(contractNumber);
        },
      });
    }
  }

  private saveCurrent() {
    this.reports.currentReport.update((report) => {
      const requestName = this.fg.controls.requestName.value;
      if (report.reportInfo) {
        return {
          ...report,
          reportInfo: { ...report.reportInfo, requestName },
        };
      }
      return report;
    });

    if (this.selectionCriteria) {
      this.selectionCriteria.updateReport();
    }
    if (this.sortOptions) {
      this.sortOptions.updateReport();
    }
  }

  previous() {
    this.saveCurrent();
    this.stepper.previous();
  }

  next() {
    this.saveCurrent();
    this.stepper.next();
  }
}
